import { Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StaffService } from '../../core/services/staff.service';
import { StaffRole } from '../../core/models/auth.model';
import { ApprovalApiError } from '../../core/errors/approval-api-error';

const ROLES: StaffRole[] = ['ADMIN', 'OFFICER', 'AUDITOR'];

@Component({
  selector: 'app-staff-create',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './staff-create.html',
  styleUrl: './staff-create.scss',
})
export class StaffCreate {
  private readonly fb = inject(FormBuilder);
  private readonly staffService = inject(StaffService);
  private readonly router = inject(Router);

  readonly roles = ROLES;
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    displayName: ['', Validators.required],
    role: ['OFFICER' as StaffRole, Validators.required],
    temporaryPassword: ['', [Validators.required, Validators.minLength(8)]],
  });

  submit(): void {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    this.staffService.createStaff(this.form.getRawValue()).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/staff']);
      },
      error: (err: unknown) => {
        this.loading.set(false);
        this.errorMessage.set(
          err instanceof ApprovalApiError ? err.message : 'Could not create staff.',
        );
      },
    });
  }
}
